import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useDebounce } from '../hooks/useDebounce';

interface SearchBarProps {
  /** Callback fired with the debounced search query (300 ms delay). */
  onSearch: (query: string) => void;
  /** Placeholder text shown inside the input. */
  placeholder?: string;
  /**
   * Accessible label for the input element.
   * Use "Tìm kiếm fanpage" on the Fanpage page and
   * "Tìm kiếm nhóm" on the Group page (Requirements 6.1, 7.1).
   */
  ariaLabel: string;
}

/**
 * SearchBar component.
 *
 * Renders a MUI TextField with a search adornment.  The raw input value is
 * debounced by 300 ms before being emitted via `onSearch`, satisfying the
 * performance requirements (Req 6.2, 7.2) and the accessibility requirement
 * for a labelled search input (Req 12.4).
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder,
  ariaLabel,
}) => {
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, 300);

  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue, onSearch]);

  return (
    <TextField
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder={placeholder ?? 'Tìm kiếm...'}
      inputProps={{ 'aria-label': ariaLabel }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {/* Unicode magnifying glass — no icon library required */}
            <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>
              🔍
            </span>
          </InputAdornment>
        ),
      }}
      size="small"
      fullWidth
    />
  );
};
